import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from './ui/icons';
import { useAuth } from './AuthContext';
import { useLoading } from './LoadingContext';
import NetworkStatusIndicator from './components/NetworkStatusIndicator';
import DeleteConfirmation from './components/DeleteConfirmationPopup';
import { useIdleLock, IDLE_LOCK_MINUTES } from './hooks/useIdleLock';
import './css/AppShell.css';

interface AppShellProps {
    children: ReactNode;
}

interface NavItem {
    key: string;
    label: string;
    to: string;
    icon: React.ReactNode;
    match: (path: string) => boolean;
}

/**
 * AppShell — the single, coherent application chrome for TableView.
 *
 * A fixed left side-rail (brand, primary navigation, user menu) plus a content
 * area. This replaces the legacy blue Toolbar, the floating logout TopFixedHeader,
 * the bottom-right FAB and the mobile drawer. Everything is scoped under
 * `.ps-scope` so the Prostay design system is the baseline for every screen.
 */
const AppShell: React.FC<AppShellProps> = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { logout, lock, staff, isAccountOwner } = useAuth();
    const { loading } = useLoading();

    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const userMenuRef = useRef<HTMLDivElement>(null);

    const [propertyName, setPropertyName] = useState<string>('');

    /**
     * Venue-configured idle timeout, in minutes. undefined until loaded (and if the
     * request fails), which keeps the built-in default — a settings hiccup must not
     * leave tablets unlocked. 0 means the venue turned auto-logout off.
     */
    const [autoLogoutMinutes, setAutoLogoutMinutes] = useState<number | undefined>(undefined);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/Company/getByPropertyId`, {
                    method: 'GET',
                    credentials: 'include',
                });
                if (!res.ok || res.status === 204) return;
                const data = await res.json();
                if (!cancelled && typeof data?.autoLogoutMinutes === 'number') {
                    setAutoLogoutMinutes(data.autoLogoutMinutes);
                }
            } catch {
                /* keep the default */
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const stored = localStorage.getItem('currentProperty');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                setPropertyName(parsed?.displayName || parsed?.name || '');
            } catch {
                /* ignore */
            }
        }
    }, [location.pathname]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            const target = event.target as Node;
            if (userMenuRef.current && !userMenuRef.current.contains(target)) {
                setUserMenuOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Clear any in-progress merge/transfer selections when changing top-level area.
    const clearTransientActions = () => {
        localStorage.removeItem('orderToMerge');
        localStorage.removeItem('tableToMergeFrom');
        localStorage.removeItem('transferFromTable');
        localStorage.removeItem('productsToTransfer');
        localStorage.removeItem('combinationsToTransfer');
    };

    const go = (to: string) => {
        clearTransientActions();
        setUserMenuOpen(false);
        navigate(to);
    };

    /**
     * Hand the device to the next person. Ends the staff session only, so the
     * venue stays signed in and the next waiter is four digits from working.
     */
    const handleLock = async () => {
        setUserMenuOpen(false);
        await lock();
        navigate('/lock', { replace: true });
    };

    /** Sign the device out of the venue entirely — a manager's job, not a waiter's. */
    const handleLogout = async () => {
        await logout();
        localStorage.clear();
        navigate('/login');
    };

    // Lock an untouched device rather than leaving it on someone's account.
    // The venue sets the delay in TableView admin (Company Settings > Auto logout after);
    // 0 turns the automatic lock off, and an unset/unreachable value keeps the built-in
    // default rather than leaving a device unlocked indefinitely.
    useIdleLock(
        handleLock,
        !!staff && autoLogoutMinutes !== 0,
        autoLogoutMinutes ?? IDLE_LOCK_MINUTES,
    );

    // TableOrder (/tables/:id) is an immersive POS surface that manages its own
    // layout; render it full-bleed until it is migrated into the shell.
    // Entry/shift surfaces (waiter selection, close-shift) live outside the rail
    // — they carry their own top chrome and shouldn't show primary navigation.
    const segments = location.pathname.split('/').filter(Boolean);
    const bareRoutes = ['lock', 'waiter-selection', 'close-shift', 'close-user-shift'];
    const isImmersive =
        (segments[0] === 'tables' && segments.length > 1) ||
        bareRoutes.includes(segments[0]);

    if (isImmersive) {
        return (
            <div className="app-shell ps-scope app-shell--immersive">
                <NetworkStatusIndicator />
                {loading && <div className="app-loadbar" />}
                <main className="app-content app-content--full">{children}</main>
            </div>
        );
    }

    const navItems: NavItem[] = [
        {
            key: 'floor',
            label: 'Floor',
            to: '/tables',
            icon: <Icon.LayoutGrid size={22} />,
            match: (p) => p === '/tables' || p.startsWith('/tables'),
        },
        {
            key: 'orders',
            label: 'Orders',
            to: '/orders',
            icon: <Icon.ClipboardList size={22} />,
            match: (p) => p.startsWith('/orders'),
        },
        {
            key: 'customers',
            label: 'Customers',
            to: '/customers',
            icon: <Icon.Users size={22} />,
            match: (p) => p.startsWith('/customers'),
        },
    ];

    // The avatar answers "who am I signed in as", which on a shared device is a
    // more useful question than which venue it is.
    const initials = (staff?.displayName || propertyName || 'TV')
        .split(' ')
        .filter(Boolean)
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

    return (
        <div className="app-shell ps-scope">
            <NetworkStatusIndicator />

            <main className="app-content">
                {loading && <div className="app-loadbar" />}
                {children}
            </main>

            {/* Floating navigation island — app-first, thumb-reachable, adapts
             * from phone to tablet. No persistent side rail. */}
            <nav className="app-dock" aria-label="Primary navigation">
                <div className="app-dock__inner">
                    {navItems.map((item) => {
                        const active = item.match(location.pathname);
                        return (
                            <button
                                key={item.key}
                                type="button"
                                className={`app-dockitem${active ? ' is-active' : ''}`}
                                onClick={() => go(item.to)}
                                aria-current={active ? 'page' : undefined}
                            >
                                <span className="app-dockitem__icon">{item.icon}</span>
                                <span className="app-dockitem__label">{item.label}</span>
                            </button>
                        );
                    })}

                    <span className="app-dock__sep" aria-hidden="true" />

                    <div className="app-dock__user" ref={userMenuRef}>
                        <button
                            type="button"
                            className={`app-dockitem app-dockitem--user${userMenuOpen ? ' is-open' : ''}`}
                            onClick={() => setUserMenuOpen((o) => !o)}
                            aria-haspopup="menu"
                            aria-expanded={userMenuOpen}
                        >
                            <span className="app-dock__avatar">{initials}</span>
                            <span className="app-dockitem__label">
                                {staff?.firstName || 'Account'}
                            </span>
                        </button>

                        {userMenuOpen && (
                            <div className="app-usermenu" role="menu">
                                {staff && (
                                    <div className="app-usermenu__who">
                                        <span className="app-usermenu__whoname">{staff.displayName}</span>
                                        <span className="app-usermenu__whorole">
                                            {[staff.roleName, propertyName].filter(Boolean).join(' · ')}
                                        </span>
                                    </div>
                                )}
                                <button className="app-usermenu__item" role="menuitem" onClick={() => go('/profile')}>
                                    <Icon.UserCircle size={18} />
                                    <span>My profile</span>
                                </button>
                                {/* The individual's own shift, not the daily close — Profile calls
                                    this same action "End Shift", so it is named to match. The
                                    daily Z-report close is "End Day" and lives at /close-shift. */}
                                <button className="app-usermenu__item" role="menuitem" onClick={() => go('/close-user-shift')}>
                                    <Icon.Clock size={18} />
                                    <span>End shift</span>
                                </button>
                                <button className="app-usermenu__item" role="menuitem" onClick={() => go('/printer-settings')}>
                                    <Icon.Printer size={18} />
                                    <span>Printer settings</span>
                                </button>
                                {/* Handing over to the next person is a one-tap button on the
                                  * floor plan, not a menu item — it happens dozens of times a
                                  * service. Signing the venue out happens once, and only the
                                  * person holding the venue's credentials is offered it. */}
                                {isAccountOwner && (
                                    <>
                                        <div className="app-usermenu__divider" />
                                        <button
                                            className="app-usermenu__item app-usermenu__item--danger"
                                            role="menuitem"
                                            onClick={() => {
                                                setUserMenuOpen(false);
                                                setShowLogoutConfirm(true);
                                            }}
                                        >
                                            <Icon.LogOut size={18} />
                                            <span>Sign device out of venue</span>
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </nav>

            {showLogoutConfirm && (
                <div className="app-logout-overlay">
                    <div
                        className="dark-popup-overlay"
                        style={{ position: 'absolute', inset: 0 }}
                        onClick={() => setShowLogoutConfirm(false)}
                    />
                    <DeleteConfirmation
                        title="Sign device out of venue"
                        content="This signs the whole device out, not just you. Getting back in needs a manager's email and password. To hand over to the next person, use Lock instead."
                        onClose={() => setShowLogoutConfirm(false)}
                        onDelete={handleLogout}
                    />
                </div>
            )}
        </div>
    );
};

export default AppShell;
